import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@material-ui/core";
import PageTitle from "../../components/PageTitle/PageTitle";
import Widget from "../../components/Widget/Widget";
import AddClassService from "./Locality/Service/addClassService";
import SectionService from "./Locality/Service/sectionService";
import StudentService from "./Locality/Service/studentService";
import AcademicYearService from "./Locality/Service/academicYearService";
import { useHistory } from "react-router-dom";

export default function Promotion() {
  const userDetails = JSON.parse(localStorage.getItem("userDetail") || "{}");
  const schooleId = userDetails?.schooleId;
  const role = String(userDetails?.role || "");
  const history = useHistory();

  useEffect(() => {
    if (role && role !== "SCHOOLE") {
      history.replace("/app/studentdetails");
    }
  }, [role, history]);

  const [mode, setMode] = useState("class"); // class | student
  const [fromClassId, setFromClassId] = useState("");
  const [fromSectionId, setFromSectionId] = useState("");
  const [toClassId, setToClassId] = useState("");
  const [toSectionId, setToSectionId] = useState("");
  const [academicYear, setAcademicYear] = useState("");
  const [academicYearOptions, setAcademicYearOptions] = useState([]);

  const [classList, setClassList] = useState([]);
  const [fromSections, setFromSections] = useState([]);
  const [toSections, setToSections] = useState([]);
  const [students, setStudents] = useState([]);
  const [selectedStudentIds, setSelectedStudentIds] = useState(new Set());
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!schooleId) return;
    AddClassService.getAllAddClass(schooleId)
      .then((res) => setClassList(Array.isArray(res) ? res : []))
      .catch(() => setClassList([]));
  }, [schooleId]);

  useEffect(() => {
    if (!schooleId) return;
    AcademicYearService.getAllAcademicYear(schooleId)
      .then((res) => {
        const list = Array.isArray(res) ? res : [];
        const active = list.filter((x) => String(x?.status || "Active").toLowerCase() === "active");
        const years = active.map((x) => String(x?.acadamicYear || "").trim()).filter(Boolean);
        const unique = Array.from(new Set(years));
        setAcademicYearOptions(unique);
        if (!academicYear && unique.length > 0) {
          setAcademicYear(unique[0]);
        }
      })
      .catch(() => setAcademicYearOptions([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schooleId]);

  const loadSections = async (classId, setter) => {
    if (!classId) {
      setter([]);
      return;
    }
    try {
      const res = await SectionService.findSectionList({ schooleId, classId });
      setter(Array.isArray(res) ? res : []);
    } catch {
      setter([]);
    }
  };

  useEffect(() => {
    loadSections(fromClassId, setFromSections);
    setFromSectionId("");
    setStudents([]);
    setSelectedStudentIds(new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromClassId]);

  useEffect(() => {
    loadSections(toClassId, setToSections);
    setToSectionId("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toClassId]);

  const fetchStudents = async () => {
    if (!schooleId || !fromClassId) return;
    setLoadingStudents(true);
    try {
      const body = { classId: fromClassId };
      if (fromSectionId) body.sectionId = fromSectionId;
      const res = await StudentService.getAllStudentById(schooleId, body);
      const list = Array.isArray(res) ? res : [];
      setStudents(list);
    } catch {
      setStudents([]);
    } finally {
      setLoadingStudents(false);
    }
  };

  useEffect(() => {
    if (mode === "student") fetchStudents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, fromClassId, fromSectionId]);

  const canPromote = useMemo(() => {
    if (!schooleId || !toClassId || !academicYear.trim()) return false;
    if (mode === "class") return Boolean(fromClassId);
    return Boolean(fromClassId) && selectedStudentIds.size > 0;
  }, [schooleId, toClassId, academicYear, mode, fromClassId, selectedStudentIds]);

  const toggleStudent = (id) => {
    setSelectedStudentIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const promote = async () => {
    if (!canPromote) return;
    if (String(fromClassId) === String(toClassId) && String(fromSectionId || "") === String(toSectionId || "")) {
      alert("From and To cannot be the same.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        schooleId,
        toClassId,
        toSectionId: toSectionId || null,
        academicYear: academicYear.trim(),
      };
      if (mode === "student") {
        payload.studentIds = Array.from(selectedStudentIds);
      } else {
        payload.fromClassId = fromClassId;
        payload.fromSectionId = fromSectionId || null;
      }
      const res = await StudentService.promoteStudents(payload);
      alert(res?.message || "Promotion done");
      if (mode === "student") {
        await fetchStudents();
        setSelectedStudentIds(new Set());
      }
    } catch (e) {
      alert(e?.response?.data?.message || e?.message || "Promotion failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <PageTitle title="Promotion" />
      <Card style={{ padding: 16, marginBottom: 12, borderLeft: "4px solid rgb(15 59 98)" }}>
        <Typography style={{ fontWeight: 700, color: "rgb(15 59 98)", marginBottom: 6 }}>
          What is Promotion?
        </Typography>
        <Typography variant="body2" style={{ color: "#374151" }}>
          Use this page to move students from one Class/Section to another for a selected Academic Year.
        </Typography>
        <Box mt={1}>
          <Typography variant="body2" style={{ color: "#374151" }}>
            <strong>How to use</strong>
          </Typography>
          <Typography variant="body2" style={{ color: "#555", marginTop: 4 }}>
            1) Select Promotion Type (entire class/section or individual students).<br />
            2) Choose <strong>From</strong> Class/Section and <strong>To</strong> Class/Section.<br />
            3) Select <strong>Academic Year</strong> (loaded from Academic Master).<br />
            4) Click <strong>Promote</strong>.
          </Typography>
          <Typography variant="body2" style={{ color: "#555", marginTop: 8 }}>
            Note: This updates students’ current Class/Section. Past attendance/activity records are not changed.
          </Typography>
        </Box>
      </Card>
      <Card style={{ padding: 16 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Box mb={1}>
              <Typography style={{ fontWeight: 700, color: "rgb(15 59 98)" }}>Promotion Type</Typography>
            </Box>
            <FormControlLabel
              control={<Checkbox checked={mode === "class"} onChange={() => setMode("class")} />}
              label="Promote entire class/section"
            />
            <FormControlLabel
              control={<Checkbox checked={mode === "student"} onChange={() => setMode("student")} />}
              label="Promote individual students"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="body2" style={{ color: "#555" }}>
              This updates students’ current Class/Section. Past attendance/activity records are not changed.
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography style={{ fontWeight: 700, marginBottom: 6 }}>From</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="dense" required>
                  <InputLabel>Class</InputLabel>
                  <Select value={fromClassId} onChange={(e) => setFromClassId(e.target.value)} label="Class">
                    <MenuItem value=""><em>None</em></MenuItem>
                    {classList.map((c) => (
                      <MenuItem key={c._id} value={c._id}>{c.className}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="dense">
                  <InputLabel>Section (optional)</InputLabel>
                  <Select
                    value={fromSectionId}
                    onChange={(e) => setFromSectionId(e.target.value)}
                    label="Section (optional)"
                    disabled={!fromClassId}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {fromSections.map((s) => (
                      <MenuItem key={s._id} value={s._id}>{s.sectionName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography style={{ fontWeight: 700, marginBottom: 6 }}>To</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="dense" required>
                  <InputLabel>Class</InputLabel>
                  <Select value={toClassId} onChange={(e) => setToClassId(e.target.value)} label="Class">
                    <MenuItem value=""><em>None</em></MenuItem>
                    {classList.map((c) => (
                      <MenuItem key={c._id} value={c._id}>{c.className}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" margin="dense">
                  <InputLabel>Section (optional)</InputLabel>
                  <Select
                    value={toSectionId}
                    onChange={(e) => setToSectionId(e.target.value)}
                    label="Section (optional)"
                    disabled={!toClassId}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {toSections.map((s) => (
                      <MenuItem key={s._id} value={s._id}>{s.sectionName}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" margin="dense" required>
              <InputLabel>Academic Year</InputLabel>
              <Select
                value={academicYear}
                onChange={(e) => setAcademicYear(e.target.value)}
                label="Academic Year"
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {academicYearOptions.map((y) => (
                  <MenuItem key={y} value={y}>
                    {y}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {mode === "student" ? (
            <Grid item xs={12}>
              <Widget title="Select Students" upperTitle noBodyPadding>
                <Box p={2}>
                  <Button
                    variant="outlined"
                    onClick={fetchStudents}
                    disabled={!fromClassId || loadingStudents}
                  >
                    {loadingStudents ? "Loading..." : "Refresh Student List"}
                  </Button>
                </Box>
                <TableContainer component={Paper} style={{ maxHeight: 420 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Select</TableCell>
                        <TableCell>Student</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Section</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {students.map((s) => {
                        const sid = String(s._id);
                        const name = `${s.firstName || ""} ${s.lastName || ""}`.trim();
                        return (
                          <TableRow key={sid}>
                            <TableCell>
                              <Checkbox checked={selectedStudentIds.has(sid)} onChange={() => toggleStudent(sid)} />
                            </TableCell>
                            <TableCell>{name || sid}</TableCell>
                            <TableCell>{s.classId?.className || ""}</TableCell>
                            <TableCell>{s.sectionId?.sectionName || ""}</TableCell>
                          </TableRow>
                        );
                      })}
                      {students.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} style={{ color: "#555" }}>
                            No students found for selected class/section.
                          </TableCell>
                        </TableRow>
                      ) : null}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Widget>
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" style={{ gap: 10 }}>
              <Button
                variant="contained"
                color="primary"
                style={{ backgroundColor: "rgb(15 59 98)", color: "white" }}
                disabled={!canPromote || saving}
                onClick={promote}
              >
                {saving ? "Promoting..." : "Promote"}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Card>
    </>
  );
}

